import { Injectable } from '@angular/core';
import { PasskeyFailureCode, PasskeyOperationError } from '../models/passkey.model';

export interface PasskeyErrorResult {
  type: 'network' | 'timeout' | 'cancellation' | 'server' | 'auth' | 'generic';
  message: string;
  shouldCountAsFailedAttempt: false;
  shouldRedirectToLogin: false;
  shouldEnablePinFallback: boolean;
  shouldSendPinAutomatically: false;
  statusCode?: number;
}

/** Clasifica errores; las pantallas deciden fallback, MFA y navegación. */
@Injectable({ providedIn: 'root' })
export class PasskeyErrorHandlerService {
  handlePasskeyError(
    error: unknown,
    context: 'login' | 'revalidation' | 'registration'
  ): PasskeyErrorResult {
    const typed = error instanceof PasskeyOperationError ? error : undefined;
    const status = typed?.status ?? (error as { status?: number } | undefined)?.status;
    const code = typed?.code ?? this.codeFromStatus(status);
    const result = this.resultFor(code, status);
    return context === 'login'
      ? { ...result, message: this.loginMessageFor(code), shouldEnablePinFallback: false }
      : result;
  }

  private loginMessageFor(code: PasskeyFailureCode): string {
    const messages: Partial<Record<PasskeyFailureCode, string>> = {
      TIMEOUT: 'La verificación tardó demasiado. Intenta de nuevo o inicia sesión con contraseña.',
      CANCELLED: 'La verificación fue cancelada. Intenta de nuevo o inicia sesión con contraseña.',
      NOT_ALLOWED: 'No se pudo completar la verificación. Intenta de nuevo o inicia sesión con contraseña.',
      NETWORK: 'No hay conexión. Reintenta o inicia sesión con contraseña.',
      SERVER: 'El servicio no está disponible. Intenta de nuevo o inicia sesión con contraseña.',
      AUTH_REJECTED: 'No se pudo verificar tu identidad. Inicia sesión con contraseña.'
    };
    return messages[code] ?? 'No se pudo verificar tu identidad. Intenta de nuevo o inicia sesión con contraseña.';
  }

  private codeFromStatus(status: number | undefined): PasskeyFailureCode {
    if (status === 0) return 'NETWORK';
    if (status === 401 || status === 403) return 'AUTH_REJECTED';
    if (typeof status === 'number' && status >= 500) return 'SERVER';
    return 'UNKNOWN';
  }

  private resultFor(code: PasskeyFailureCode, status?: number): PasskeyErrorResult {
    const results: Partial<Record<PasskeyFailureCode, PasskeyErrorResult>> = {
      TIMEOUT: this.result('timeout', 'La verificación tardó demasiado. Intenta de nuevo o usa el PIN.', true, status),
      CANCELLED: this.result('cancellation', 'La verificación fue cancelada. Puedes reintentar o usar el PIN.', true, status),
      NOT_ALLOWED: this.result('cancellation', 'No se pudo completar la verificación. Puedes reintentar o usar el PIN.', true, status),
      NETWORK: this.result('network', 'No hay conexión. Reintenta o usa el PIN.', true, status),
      SERVER: this.result('server', 'El servicio no está disponible. Intenta de nuevo o usa el PIN.', true, status),
      AUTH_REJECTED: this.result('auth', 'No se pudo verificar tu identidad. Usa el PIN o inicia sesión nuevamente.', true, status)
    };
    return results[code] ?? this.result('generic', 'No se pudo verificar tu identidad. Reintenta o usa el PIN.', true, status);
  }

  private result(
    type: PasskeyErrorResult['type'], message: string, shouldEnablePinFallback: boolean, statusCode?: number
  ): PasskeyErrorResult {
    return { type, message, shouldCountAsFailedAttempt: false, shouldRedirectToLogin: false,
      shouldEnablePinFallback, shouldSendPinAutomatically: false, statusCode };
  }
}
